import { useEffect, useState, type ChangeEvent, type JSX } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { ASSET_CATALOG, catalogBySection, type CatalogEntry } from '../assets/catalog';
import { ConfirmDialog } from './ConfirmDialog';
import { Lightbox } from './Lightbox';
import {
  deleteExample,
  FOR_MAXIM_SECTION,
  isImageFileName,
  isVideoFileName,
  listExamples,
  listOverrides,
  listVersions,
  rollbackToVersion,
  uploadAssetVersion,
  uploadExample,
  type ExampleRecord,
  type OverrideRecord,
  type SheetMeta,
  type VersionRecord,
} from './api';

const EXAMPLES_TAB = 'Examples';
const TODO_TAB = 'TODO';
const FOR_MAXIM_TAB = FOR_MAXIM_SECTION;
const PSD_TAB = 'PSD';
const SPARKY_DONORS_TAB = 'Sparky: DONORS';
// Free-form image sections (not game assets) - discriminated by ExampleRecord.section.
const FREEFORM_TABS = [EXAMPLES_TAB, TODO_TAB, FOR_MAXIM_TAB, PSD_TAB, SPARKY_DONORS_TAB];

// Top-level game-asset sections (Character / Arena / UI / Website), each with
// its own sub-catalogs.
const SECTIONS = catalogBySection();

// Website (site assets) is split off from the game sections by a divider, so
// it is visually clear which assets belong to the site vs the game.
const WEBSITE_SECTION = 'Website';
const TAB_DIVIDER = '__divider__';
const websiteTabs = SECTIONS.filter((s) => s.section === WEBSITE_SECTION).map((s) => s.section);
const gameTabs = SECTIONS.filter((s) => s.section !== WEBSITE_SECTION).map((s) => s.section);
const TABS = ['All', ...websiteTabs, TAB_DIVIDER, ...gameTabs, ...FREEFORM_TABS];

// Asset browser: category tabs over a grid of asset cards. Each card previews
// the current asset (uploaded override or bundled default), downloads it, and
// uploads a new version. The Examples tab lists reference images uploaded with
// tickets (not game assets).
export function AssetsView({
  userEmail,
  isAdmin,
}: {
  userEmail: string;
  isAdmin: boolean;
}): JSX.Element {
  const [overrides, setOverrides] = useState<Map<string, OverrideRecord>>(new Map());
  const [examples, setExamples] = useState<ExampleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState<{ images: string[]; index: number } | null>(null);
  const [tab, setTab] = useState('All');

  // Open the lightbox on `url` within `images`, so Left/Right can step the set.
  const openImages = (images: string[], url: string): void => {
    const at = images.indexOf(url);
    setZoom({ images, index: at >= 0 ? at : 0 });
  };
  const [confirmDeleteExample, setConfirmDeleteExample] = useState<ExampleRecord | null>(null);
  const [uploadingExample, setUploadingExample] = useState(false);

  const onUploadExamples = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    // Snapshot the files BEFORE clearing value - resetting input.value empties
    // the same live FileList, which would otherwise make files look empty.
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (files.length === 0) return;
    setUploadingExample(true);
    setError('');
    try {
      for (const file of files) {
        await uploadExample({ file, section: tab, userEmail });
      }
      refresh();
    } catch (err) {
      setError(String(err));
    } finally {
      setUploadingExample(false);
    }
  };

  const refresh = (): void => {
    listOverrides()
      .then((m) => {
        setOverrides(m);
        setError('');
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
    listExamples()
      .then(setExamples)
      .catch(() => {});
  };

  useEffect(refresh, []);

  if (loading) return <div className="placeholder">Loading assets...</div>;

  const isFreeform = FREEFORM_TABS.includes(tab);
  const freeformItems = examples.filter((ex) => (ex.section ?? EXAMPLES_TAB) === tab);
  const currentSection = SECTIONS.find((s) => s.section === tab);
  // Show sub-headers only when a section actually has more than one sub-catalog.
  const showSubheads = (currentSection?.subgroups.length ?? 0) > 1;

  // Ordered image urls visible in the current tab - the set arrows step through.
  // For game-asset tabs it spans every sub-catalog, not just one subgroup.
  const sectionEntries: readonly CatalogEntry[] =
    tab === 'All' ? ASSET_CATALOG : (currentSection?.subgroups ?? []).flatMap((sg) => sg.items);
  const gridImages = sectionEntries
    .filter((e) => e.kind !== 'audio')
    .map((e) => overrides.get(e.key)?.url ?? e.key);
  const freeformImages = freeformItems
    .map((ex) => (isImageFileName(ex.name) || isVideoFileName(ex.name) ? ex.url : ex.thumbUrl))
    .filter((u): u is string => !!u);

  const renderGrid = (entries: readonly CatalogEntry[]): JSX.Element => (
    <div className="asset-grid">
      {entries.map((entry) => (
        <AssetRow
          key={entry.key}
          entry={entry}
          override={overrides.get(entry.key)}
          userEmail={userEmail}
          onChanged={refresh}
          onZoom={(url) => openImages(gridImages, url)}
          openImages={openImages}
        />
      ))}
    </div>
  );

  return (
    <div className="assets">
      <div className="asset-tabs">
        {TABS.map((t) =>
          t === TAB_DIVIDER ? (
            <span key={t} className="tab-divider" aria-hidden />
          ) : (
            <button
              key={t}
              className={`picker-tab ${t === tab ? 'active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t}
            </button>
          ),
        )}
      </div>

      {error && <div className="error">{error}</div>}

      {isFreeform ? (
        <>
          <div className="example-toolbar">
            <label className="btn">
              <i className="fas fa-upload" /> {uploadingExample ? 'Uploading...' : `Add ${tab}`}
              <input
                type="file"
                accept="image/*,video/mp4,.mp4,.psd,.ai,.eps,.pdf"
                multiple
                hidden
                disabled={uploadingExample}
                onChange={onUploadExamples}
              />
            </label>
          </div>
          <div className="asset-grid">
            {freeformItems.length === 0 && <div className="muted">No {tab} yet.</div>}
            {freeformItems.map((ex) => (
              <ExampleCard
                key={ex.id}
                example={ex}
                isAdmin={isAdmin}
                onZoom={(url) => openImages(freeformImages, url)}
                onDelete={setConfirmDeleteExample}
              />
            ))}
          </div>
        </>
      ) : tab === 'All' ? (
        renderGrid(ASSET_CATALOG)
      ) : (
        (currentSection?.subgroups ?? []).map((sg) => (
          <div key={sg.subgroup} className="asset-subsection">
            {showSubheads && <h4 className="asset-subhead">{sg.subgroup}</h4>}
            {renderGrid(sg.items)}
          </div>
        ))
      )}

      {zoom && (
        <Lightbox
          images={zoom.images}
          index={zoom.index}
          onIndexChange={(next) => setZoom((z) => (z ? { ...z, index: next } : z))}
          onClose={() => setZoom(null)}
        />
      )}
      {confirmDeleteExample && (
        <ConfirmDialog
          title="Delete example?"
          message={confirmDeleteExample.name}
          confirmLabel="Delete"
          onConfirm={() => {
            const id = confirmDeleteExample.id;
            setConfirmDeleteExample(null);
            deleteExample(id)
              .then(refresh)
              .catch((e) => setError(String(e)));
          }}
          onCancel={() => setConfirmDeleteExample(null)}
        />
      )}
    </div>
  );
}

// One reference image in the Examples tab. Preview + download + admin delete.
function ExampleCard({
  example,
  isAdmin,
  onZoom,
  onDelete,
}: {
  example: ExampleRecord;
  isAdmin: boolean;
  onZoom: (url: string) => void;
  onDelete: (ex: ExampleRecord) => void;
}): JSX.Element {
  return (
    <div className="asset-card">
      {isImageFileName(example.name) ? (
        <Preview kind="image" url={example.url} onZoom={onZoom} />
      ) : isVideoFileName(example.name) ? (
        <video
          className="preview zoomable"
          src={`${example.url}#t=0.1`}
          muted
          preload="metadata"
          title="Click to play"
          onClick={() => onZoom(example.url)}
        />
      ) : example.thumbUrl ? (
        <Preview kind="image" url={example.thumbUrl} onZoom={onZoom} />
      ) : (
        <FilePreview name={example.name} />
      )}
      <div className="asset-info">
        <div className="asset-label">{example.name}</div>
        {example.uploadedBy && <div className="asset-meta muted">{example.uploadedBy}</div>}
        <div className="asset-actions">
          <a className="btn" href={example.url} download target="_blank" rel="noreferrer">
            <i className="fas fa-download" /> Download
          </a>
          {isAdmin && (
            <button className="btn-danger" onClick={() => onDelete(example)}>
              <i className="fas fa-trash" /> Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AssetRow(props: {
  entry: CatalogEntry;
  override: OverrideRecord | undefined;
  userEmail: string;
  onChanged: () => void;
  onZoom: (url: string) => void;
  openImages: (images: string[], url: string) => void;
}): JSX.Element {
  const { entry, override, userEmail, onChanged, onZoom, openImages } = props;
  const currentUrl = override?.url ?? entry.key;
  const cols = override?.cols ?? entry.cols;
  const rows = override?.rows ?? entry.rows;

  const [file, setFile] = useState<File | null>(null);
  const [meta, setMeta] = useState<SheetMeta>({ cols, rows, autoTrim: true });
  const [busy, setBusy] = useState(false);
  const [rowError, setRowError] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const onUpload = async (): Promise<void> => {
    if (!file) return;
    setBusy(true);
    setRowError('');
    try {
      await uploadAssetVersion({ entry, file, meta: entry.kind === 'sheet' ? meta : undefined, userEmail });
      setFile(null);
      onChanged();
    } catch (e) {
      setRowError(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="asset-card">
      <Preview kind={entry.kind} url={currentUrl} onZoom={onZoom} />
      <div className="asset-info">
        <div className="asset-label">{entry.label}</div>
        <div className="asset-meta">
          <span className={`badge ${entry.kind}`}>{entry.kind}</span>
          {entry.kind === 'sheet' && (
            <span className="grid">
              {cols}x{rows}
            </span>
          )}
          {override && <span className="badge overridden">overridden</span>}
        </div>

        <div className="asset-actions">
          <a className="btn" href={currentUrl} download target="_blank" rel="noreferrer">
            <i className="fas fa-download" /> Download
          </a>
          <label className="btn">
            <i className="fas fa-upload" /> Replace
            <input
              type="file"
              accept={entry.kind === 'audio' ? 'audio/*' : 'image/*'}
              hidden
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <button className="link" onClick={() => setShowHistory(true)}>
            <i className="fas fa-history" /> history
          </button>
        </div>

        {file && (
          <div className="upload-form">
            <span className="file-name">{file.name}</span>
            {entry.kind === 'sheet' && (
              <span className="grid-inputs">
                <label>
                  cols
                  <input
                    type="number"
                    min={1}
                    value={meta.cols ?? ''}
                    onChange={(e) => setMeta((m) => ({ ...m, cols: numOrUndef(e.target.value) }))}
                  />
                </label>
                <label>
                  rows
                  <input
                    type="number"
                    min={1}
                    value={meta.rows ?? ''}
                    onChange={(e) => setMeta((m) => ({ ...m, rows: numOrUndef(e.target.value) }))}
                  />
                </label>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={meta.autoTrim ?? true}
                    onChange={(e) => setMeta((m) => ({ ...m, autoTrim: e.target.checked }))}
                  />
                  autoTrim
                </label>
              </span>
            )}
            <button className="primary" disabled={busy} onClick={onUpload}>
              <i className="fas fa-upload" /> {busy ? 'Uploading...' : 'Upload new version'}
            </button>
            <button className="btn-danger" disabled={busy} onClick={() => setFile(null)}>
              Cancel
            </button>
          </div>
        )}

        {rowError && <div className="error">{rowError}</div>}
        {showHistory && (
          <HistoryModal
            entry={entry}
            userEmail={userEmail}
            onChanged={onChanged}
            openImages={openImages}
            onClose={() => setShowHistory(false)}
          />
        )}
      </div>
    </div>
  );
}

// Version history for one asset, in a modal (the inline list doesn't fit the
// narrow card). Picking a version repoints the override and closes.
function HistoryModal(props: {
  entry: CatalogEntry;
  userEmail: string;
  onChanged: () => void;
  openImages: (images: string[], url: string) => void;
  onClose: () => void;
}): JSX.Element {
  const { entry, userEmail, onChanged, openImages, onClose } = props;
  const [versions, setVersions] = useState<VersionRecord[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    listVersions(entry.key)
      .then(setVersions)
      .catch((e) => setError(String(e)));
  }, [entry.key]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <RemoveScroll>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-card" onClick={(e) => e.stopPropagation()}>
          <div className="modal-head">
            <strong>History - {entry.label}</strong>
            <button className="modal-close" onClick={onClose} title="Close">
              <i className="fas fa-times" />
            </button>
          </div>
          {error && <div className="error">{error}</div>}
          {!versions ? (
            <div className="muted">Loading history...</div>
          ) : versions.length === 0 ? (
            <div className="muted">No uploads yet.</div>
          ) : (
            <div className="history">
              {versions.map((v) => (
                <div key={v.id} className="history-row">
                  <Preview
                    kind={v.kind}
                    url={v.url}
                    small
                    onZoom={(url) =>
                      openImages(
                        versions.filter((x) => x.kind !== 'audio').map((x) => x.url),
                        url,
                      )
                    }
                  />
                  <span className="muted">
                    {v.uploadedAtMs ? new Date(v.uploadedAtMs).toLocaleString() : 'pending'} -{' '}
                    {v.uploadedBy}
                    {v.kind === 'sheet' && v.cols ? ` - ${v.cols}x${v.rows}` : ''}
                  </span>
                  <button
                    className="btn"
                    onClick={() =>
                      rollbackToVersion({ version: v, userEmail })
                        .then(() => {
                          onChanged();
                          onClose();
                        })
                        .catch((e) => setError(String(e)))
                    }
                  >
                    <i className="fas fa-undo" /> Use this
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </RemoveScroll>
  );
}

function Preview({
  kind,
  url,
  small,
  onZoom,
}: {
  kind: CatalogEntry['kind'];
  url: string;
  small?: boolean;
  onZoom?: (url: string) => void;
}): JSX.Element {
  if (kind === 'audio') {
    return (
      <span className={small ? 'preview-audio small' : 'preview-audio'}>
        <audio controls src={url} />
      </span>
    );
  }
  return (
    <img
      className={`${small ? 'preview small' : 'preview'} zoomable`}
      src={url}
      alt=""
      loading="lazy"
      title="Click to view fullscreen"
      onClick={() => onZoom?.(url)}
    />
  );
}

// Placeholder for non-image files (.psd/.ai/...) - no thumbnail, just the type.
function FilePreview({ name }: { name: string }): JSX.Element {
  const ext = name.split('.').pop()?.toUpperCase() ?? 'FILE';
  return (
    <div className="preview file-preview" title={name}>
      <i className="fas fa-file" />
      <span className="file-ext">{ext}</span>
    </div>
  );
}

function numOrUndef(v: string): number | undefined {
  const n = Number(v);
  return v === '' || Number.isNaN(n) ? undefined : n;
}
